import React, { useEffect, useMemo, useState } from 'react';
import { Bell, BellOff, ShieldCheck, Trash2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import accountService, { AccountDeletionStatus } from '../services/account.service';
import {
  getNativeDeviceId,
  getStoredNativePushToken,
  isNativeMobileRuntime,
  registerNativePushNotifications,
} from '../services/native-mobile.service';

const AccountPrivacyPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const currentUser = useMemo(() => ((user as any)?.user ? (user as any).user : user), [user]);
  const [deletionStatus, setDeletionStatus] = useState<AccountDeletionStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [notice, setNotice] = useState('');
  const [notificationBusy, setNotificationBusy] = useState(false);
  const [deletionBusy, setDeletionBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [showDeletionConfirm, setShowDeletionConfirm] = useState(false);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        setLoadingStatus(true);
        setDeletionStatus(await accountService.getAccountDeletionStatus());
      } catch {
        setDeletionStatus(null);
      } finally {
        setLoadingStatus(false);
      }
    };

    void loadStatus();
  }, []);

  const enableNotifications = async () => {
    try {
      setNotificationBusy(true);
      setNotice('');
      const registration = await registerNativePushNotifications();
      if (registration.status !== 'registered') {
        setNotice(registration.message);
        return;
      }

      await accountService.registerPushDevice(registration.payload);
      setNotice('Notifications are enabled for this device.');
    } catch (error: any) {
      setNotice(error?.message || 'Unable to enable notifications right now.');
    } finally {
      setNotificationBusy(false);
    }
  };

  const disableNotifications = async () => {
    try {
      setNotificationBusy(true);
      setNotice('');
      await accountService.disablePushDevice({
        deviceId: getNativeDeviceId(),
        pushToken: getStoredNativePushToken(),
      });
      setNotice('Notifications are disabled for this device.');
    } catch (error: any) {
      const text = error?.response?.data?.message || 'Unable to disable notifications right now.';
      setNotice(typeof text === 'string' ? text : 'Unable to disable notifications right now.');
    } finally {
      setNotificationBusy(false);
    }
  };

  const requestDeletion = async () => {
    try {
      setDeletionBusy(true);
      setNotice('');
      const response = await accountService.requestAccountDeletion({
        confirmation,
        reason,
      });
      logout();
      navigate(`/account-deletion?reference=${encodeURIComponent(response.referenceNumber)}`, { replace: true });
    } catch (error: any) {
      const text = error?.response?.data?.message || 'Unable to request account deletion right now.';
      setNotice(typeof text === 'string' ? text : 'Unable to request account deletion right now.');
    } finally {
      setDeletionBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-[linear-gradient(135deg,_#020617_0%,_#1e293b_55%,_#0f766e_100%)] px-6 py-7 text-white shadow-[0_30px_90px_-48px_rgba(15,23,42,0.55)] sm:px-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-teal-100">Privacy & Account</p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight">Settings</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-200">
              Manage notification preferences, account deletion, and privacy links for your LocalAlly account.
            </p>
          </div>
          <Link
            to="/privacy"
            className="inline-flex items-center justify-center rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
          >
            Privacy Policy
          </Link>
        </div>
      </section>

      {notice && (
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-800">
          {notice}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(300px,0.9fr)]">
        <section className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.35)]">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700">
              <ShieldCheck className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Account</p>
              <h2 className="mt-1 text-xl font-semibold text-slate-950">Current account</h2>
              <p className="mt-1 text-sm text-slate-600">
                {[currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ') || 'LocalAlly user'}
                {currentUser?.role ? ` | ${String(currentUser.role).toLowerCase()} role` : ''}
              </p>
            </div>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              Account deletion status
              <span className="mt-1 block font-semibold text-slate-950">
                {loadingStatus ? 'Loading...' : deletionStatus?.status || 'NONE'}
              </span>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              Reference
              <span className="mt-1 block font-semibold text-slate-950">
                {deletionStatus?.referenceNumber || 'Not requested'}
              </span>
            </div>
          </div>
        </section>

        <section className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.35)]">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-50 text-sky-700">
              <Bell className="h-5 w-5" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Notifications</p>
              <h2 className="mt-1 text-xl font-semibold text-slate-950">Device alerts</h2>
              <p className="mt-1 text-sm text-slate-600">
                Enable reminders for demo classes, exams, certificates, subscriptions, and account updates.
              </p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={enableNotifications}
              disabled={notificationBusy || !isNativeMobileRuntime()}
              className="inline-flex items-center justify-center rounded-full bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Bell className="mr-2 h-4 w-4" aria-hidden="true" />
              Enable
            </button>
            <button
              type="button"
              onClick={disableNotifications}
              disabled={notificationBusy}
              className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <BellOff className="mr-2 h-4 w-4" aria-hidden="true" />
              Disable
            </button>
          </div>
          {!isNativeMobileRuntime() && (
            <p className="mt-3 text-xs text-slate-500">Native notification permission is available inside the Android app.</p>
          )}
        </section>
      </div>

      <section className="rounded-[1.75rem] border border-rose-200 bg-white p-5 shadow-[0_20px_60px_-45px_rgba(15,23,42,0.35)]">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-50 text-rose-700">
            <Trash2 className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-rose-700">Delete Account</p>
            <h2 className="mt-1 text-xl font-semibold text-slate-950">Delete your account</h2>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">
              This disables login, hides public student/tutor profile visibility, removes notification tokens, and records a reference number. You can restore the account within 30 days. Payment, exam, certificate, dispute, legal, and safety records may be retained where required.
            </p>
          </div>
        </div>

        <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
          <div>
            <label className="block text-sm font-medium text-slate-700">Reason</label>
            <textarea
              rows={4}
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                setShowDeletionConfirm(false);
              }}
              placeholder="Optional"
              className="mt-2 block w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-950 outline-none transition focus:border-slate-400 focus:bg-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Type DELETE</label>
            <input
              value={confirmation}
              onChange={(event) => {
                setConfirmation(event.target.value);
                setShowDeletionConfirm(false);
              }}
              className="mt-2 block w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-semibold text-slate-950 outline-none transition focus:border-slate-400 focus:bg-white"
            />
            <button
              type="button"
              onClick={() => setShowDeletionConfirm(true)}
              disabled={deletionBusy || confirmation.trim().toUpperCase() !== 'DELETE'}
              className="mt-4 inline-flex w-full items-center justify-center rounded-full bg-rose-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {deletionBusy ? 'Deleting...' : 'Delete Account'}
            </button>
          </div>
        </div>

        {showDeletionConfirm && (
          <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-4 text-sm text-rose-900">
            <p className="font-semibold">Are you sure you want to delete your LocalAlly account?</p>
            <p className="mt-1">
              Your profile, connections, and activity will be hidden. You can restore your account within 30 days.
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={requestDeletion}
                disabled={deletionBusy}
                className="inline-flex items-center justify-center rounded-full bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deletionBusy ? 'Deleting...' : 'Confirm Delete'}
              </button>
              <button
                type="button"
                onClick={() => setShowDeletionConfirm(false)}
                disabled={deletionBusy}
                className="inline-flex items-center justify-center rounded-full border border-rose-200 bg-white px-5 py-2.5 text-sm font-semibold text-rose-800 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default AccountPrivacyPage;
